import {
  computed,
  onBeforeUnmount,
  shallowRef,
  toValue,
  watch,
  type MaybeRefOrGetter,
  type Ref,
} from 'vue'
import { viewportToScaleRootPoint } from '@/composables/useZoomScale'

export interface UseDeviceCardClickTipOptions {
  /** 当前卡片是否为激活态 */
  isActive: MaybeRefOrGetter<boolean>
  /** 是否正在展示设备概览（概览时不显示提示） */
  isOverviewActive: MaybeRefOrGetter<boolean>
  /** 卡片根节点，用于关闭概览后检测 :hover */
  cardRef: Ref<HTMLElement | null>
  /** 提示相对指针的偏移 */
  offset?: { x?: number; y?: number }
  /** 跟手插值系数：越大越贴指针，越小越平滑拖尾 */
  followLerp?: number
}

/**
 * 设备卡片「点击进入」跟随指针提示
 * - 仅在 isActive 且悬停卡片时显示
 * - 进入/离开由组件侧 Transition 处理
 * - 概览打开/关闭时正确恢复悬停态
 */
export function useDeviceCardClickTip(options: UseDeviceCardClickTipOptions) {
  const { isActive, isOverviewActive, cardRef, offset, followLerp = 0.42 } = options
  const tipOffsetX = offset?.x ?? 16
  const tipOffsetY = offset?.y ?? 16

  const isHovering = shallowRef(false)
  const isOverActions = shallowRef(false)

  /** 指针目标位置（mousemove 即时写入） */
  const targetX = shallowRef(0)
  const targetY = shallowRef(0)
  /** 提示实际渲染位置（rAF 插值） */
  const tipX = shallowRef(0)
  const tipY = shallowRef(0)

  let rafId = 0

  const showClickTip = computed(
    () =>
      toValue(isActive) && isHovering.value && !isOverActions.value && !toValue(isOverviewActive),
  )

  const tipStyle = computed(() => ({
    transform: `translate3d(${tipX.value}px, ${tipY.value}px, 0)`,
  }))

  const stopFollowLoop = () => {
    if (rafId) {
      cancelAnimationFrame(rafId)
      rafId = 0
    }
  }

  const followTick = () => {
    const dx = targetX.value - tipX.value
    const dy = targetY.value - tipY.value

    if (Math.abs(dx) < 0.15 && Math.abs(dy) < 0.15) {
      tipX.value = targetX.value
      tipY.value = targetY.value
      rafId = 0
      return
    }

    tipX.value += dx * followLerp
    tipY.value += dy * followLerp
    rafId = requestAnimationFrame(followTick)
  }

  const syncTipToPointer = (clientX: number, clientY: number, snap = false) => {
    const point = viewportToScaleRootPoint(clientX, clientY)
    targetX.value = point.x + tipOffsetX
    targetY.value = point.y + tipOffsetY
    if (snap) {
      tipX.value = targetX.value
      tipY.value = targetY.value
      stopFollowLoop()
      return
    }
    if (!rafId) {
      rafId = requestAnimationFrame(followTick)
    }
  }

  const onCardPointerEnter = (e: PointerEvent) => {
    if (!toValue(isActive)) return
    syncTipToPointer(e.clientX, e.clientY, true)
    isHovering.value = true
  }

  const onCardPointerLeave = () => {
    isHovering.value = false
    isOverActions.value = false
    stopFollowLoop()
  }

  const onCardPointerMove = (e: PointerEvent) => {
    if (!toValue(isActive) || !isHovering.value) return
    // 概览打开时也持续同步，关闭后提示能出现在当前指针旁
    syncTipToPointer(e.clientX, e.clientY)
  }

  const resetHoverState = () => {
    isHovering.value = false
    isOverActions.value = false
    stopFollowLoop()
  }

  watch(
    () => toValue(isActive),
    (active) => {
      if (!active) {
        resetHoverState()
      }
    },
  )

  watch(
    () => toValue(isOverviewActive),
    (overviewActive) => {
      if (overviewActive) {
        // 从按钮区切入概览时，按钮节点卸载可能收不到 pointerleave
        isOverActions.value = false
        stopFollowLoop()
        return
      }

      // 关闭概览后鼠标可能仍在卡内，不会再触发 pointerenter，需补回悬停态
      if (toValue(isActive) && cardRef.value?.matches(':hover')) {
        isHovering.value = true
      }
    },
  )

  onBeforeUnmount(stopFollowLoop)

  return {
    showClickTip,
    tipStyle,
    isOverActions,
    onCardPointerEnter,
    onCardPointerLeave,
    onCardPointerMove,
    resetHoverState,
  }
}
